import { useEffect, useRef, useState } from 'react';
import ShelfQrScanner from './ShelfQrScanner';
import LabelDesigner from './LabelDesigner';
import { useTranslation } from '../hooks/useTranslation';
import { hasCustomFeature } from '../services/customFeatures';
import { setProfileSyncPaused } from '../services/accountProfile';
import {
  isPrinterSupported, getPrinterConfig, setPrinterConfig, isAutoPrintEnabled, setAutoPrintEnabled,
  listPairedPrinters, testPrint, parsePrinterCode, requestBtPermission, isBluetoothEnabled,
  getPrinterLang, setPrinterLang, guessPrinterLang, getLabelCfg, saveLabelCfg, isNetworkAddress,
} from '../services/printerService';

/**
 * 🖨️ Drucker einrichten — geführt statt als lange Einstellungsliste:
 *   Verbindung wählen → Drucker antippen → Testdruck („Ist ein lesbares Etikett gekommen?") → Etikett gestalten.
 * Den Druckertyp muss niemand kennen: Kommt beim ersten Testdruck nichts Lesbares, probiert die App
 * die andere Druckersprache selbst aus.
 * Ist schon ein Drucker eingerichtet, öffnet die Übersicht (Testdruck, Etikett, anderer Drucker).
 *
 * Die Kopplung gilt nur für dieses Gerät; die Etikett-Einstellungen gehören zum Konto-Profil.
 */
export default function PrinterWizard({ colors, onClose, onClassic }) {
  const { t } = useTranslation();
  const dark = colors?.darkMode;
  const c = { bg: dark ? '#23272f' : '#fff', text: dark ? '#fff' : '#23272f', sub: dark ? '#aaa' : '#666', rand: dark ? '#444' : '#ddd', box: dark ? '#2d333b' : '#f5f6f8' };
  const supported = isPrinterSupported();

  const [drucker, setDrucker] = useState(getPrinterConfig());
  const [view, setView] = useState(() => (getPrinterConfig()?.address ? 'hub' : 'connect')); // hub | connect | bt | net | qr | test | label
  const [devices, setDevices] = useState(null); // null = lädt
  const [btAn, setBtAn] = useState(true);
  const [berechtigt, setBerechtigt] = useState(true); // Android-Berechtigung „Geräte in der Nähe"
  const [fehler, setFehler] = useState('');
  const [alleZeigen, setAlleZeigen] = useState(false);
  const [hilfe, setHilfe] = useState(false);
  const [mac, setMac] = useState('');
  const [ip, setIp] = useState('');
  const [test, setTest] = useState({ phase: 'idle', versuch: 1, lang: getPrinterLang() }); // sending | ask | error | failed
  const [autoPrint, setAutoPrint] = useState(isAutoPrintEnabled());
  const [cfg, setCfg] = useState(getLabelCfg());
  const [erweitert, setErweitert] = useState(false);
  const lebt = useRef(true);
  const nachTest = useRef('auto'); // 'hub' = Testdruck aus der Übersicht → danach wieder dorthin
  const viewRef = useRef(view);
  viewRef.current = view;
  useEffect(() => {
    const onProfile = () => {
      if (viewRef.current !== 'label') setCfg(getLabelCfg());
      setAutoPrint(isAutoPrintEnabled());
    };
    window.addEventListener('kiosk:profile-applied', onProfile);
    return () => { lebt.current = false; setProfileSyncPaused(false); window.removeEventListener('kiosk:profile-applied', onProfile); };
  }, []);

  const errText = (e, fb) => { const m = e?.message || ''; return /berechtigung|permission/i.test(m) ? t('printer.permDenied') : (m || fb); };

  const ladeGeraete = async () => {
    setFehler(''); setDevices(null);
    try {
      // Erst die Voraussetzungen prüfen und anzeigen — daran scheitert das Einrichten am häufigsten
      const ok = await requestBtPermission();   // zeigt beim ersten Mal den Android-Dialog
      const an = await isBluetoothEnabled();
      if (!lebt.current) return;
      setBerechtigt(ok); setBtAn(an);
      if (!ok) { setDevices([]); return; }
      const list = await listPairedPrinters();
      if (!lebt.current) return;
      setDevices(list);
    } catch (e) {
      if (!lebt.current) return;
      setDevices([]); setFehler(errText(e, t('printer.loadFailed')));
    }
  };
  useEffect(() => { if (view === 'bt') ladeGeraete(); }, [view]); // eslint-disable-line

  // ── Testdruck: erst die vermutete Druckersprache, bei „Nein" die andere ──
  const startLang = useRef(getPrinterLang());
  const sende = async (address, lang, versuch) => {
    if (versuch === 1) startLang.current = lang;
    setProfileSyncPaused(true);
    setTest({ phase: 'sending', versuch, lang });
    setPrinterLang(lang);
    try {
      await testPrint(address, { direkt: true });
      if (lebt.current) setTest({ phase: 'ask', versuch, lang });
    } catch (e) {
      if (versuch === 2) setPrinterLang(startLang.current);
      setProfileSyncPaused(false);
      if (lebt.current) setTest({ phase: 'error', versuch: 1, lang: startLang.current, meldung: errText(e, t('printer.testFailed')) });
    }
  };
  const waehle = (config, langTipp) => {
    setPrinterConfig(config); setDrucker(config); setFehler('');
    nachTest.current = 'auto';
    setView('test');
    sende(config.address, langTipp, 1);
  };
  const waehleGeraet = (d) => waehle({ address: d.address, name: d.name || d.address }, guessPrinterLang(d.name) || 'cpcl');
  const waehleCode = (text) => {
    const p = parsePrinterCode(text);
    if (!p) { setFehler(t('printer.noMac')); return false; }
    if (p.network) waehle({ address: p.address, name: `${t('printer.networkName')} ${p.address.replace(/^tcp:\/\//i, '')}` }, 'zpl');
    else waehle({ address: p.address, name: t('printer.defaultName') }, 'cpcl');
    return true;
  };
  const antwortJa = () => {
    setProfileSyncPaused(false);
    const neu = { ...getLabelCfg() };
    setCfg(neu);
    try { window.dispatchEvent(new CustomEvent('kiosk:label-cfg-changed')); } catch { /* */ }
    setTest((x) => ({ ...x, phase: 'idle' }));
    setView(nachTest.current !== 'hub' && neu.lang === 'zpl' ? 'label' : 'hub');
  };
  const antwortNein = () => {
    if (test.versuch === 1) {
      sende(drucker.address, test.lang === 'zpl' ? 'cpcl' : 'zpl', 2);
    } else {
      const orig = guessPrinterLang(drucker?.name) || startLang.current;
      setPrinterLang(orig);
      setProfileSyncPaused(false);
      setCfg((c0) => ({ ...c0, lang: orig }));
      setTest({ phase: 'failed', versuch: 1, lang: orig });
    }
  };
  const nochmal = () => sende(drucker.address, guessPrinterLang(drucker?.name) || startLang.current || test.lang, 1);

  const cfgAendern = (neu) => { setCfg(neu); saveLabelCfg(neu); };
  const entfernen = () => { setPrinterConfig(null); setDrucker(null); setView('connect'); };

  // ── Bausteine ──
  const btn = (primary, extra = {}) => ({
    width: '100%', padding: '13px', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 10, boxSizing: 'border-box',
    border: primary ? 'none' : `1px solid ${c.rand}`, background: primary ? '#1976d2' : 'transparent', color: primary ? '#fff' : c.text, ...extra,
  });
  const kachel = { display: 'block', width: '100%', textAlign: 'left', padding: '14px', marginTop: 10, borderRadius: 12, cursor: 'pointer',
    border: `2px solid ${c.rand}`, background: 'transparent', color: c.text, fontSize: 15, boxSizing: 'border-box' };
  const klein = { fontSize: 13, color: c.sub, lineHeight: 1.5 };
  const zurueck = (ziel) => (
    <button type="button" data-testid="pw-back" onClick={() => { setFehler(''); setView(ziel); }} style={btn(false)}>← {t('setup.back')}</button>
  );
  const erkannt = (d) => !!guessPrinterLang(d.name);
  const typName = (lang) => (lang === 'zpl' ? t('setup.printer.desktop') : t('setup.printer.mobile'));

  const titel = { hub: t('pw.hubTitle'), connect: t('pw.connectTitle'), bt: t('pw.btTitle'), net: t('pw.netTitle'), qr: t('printer.scanTitle'), test: t('pw.testTitle'), label: t('pw.labelTitle') }[view];

  let inhalt = null;
  if (!supported) {
    // Kein Android-Gerät: koppeln geht hier nicht, das Etikett lässt sich trotzdem gestalten
    inhalt = (
      <>
        <div style={klein}>{t('pw.notAndroid')}</div>
        <div style={{ marginTop: 14 }}>
          <LabelDesigner value={cfg} onChange={cfgAendern} t={t} colors={colors} nachnameErlaubt={hasCustomFeature('nachnameSortierung')} />
        </div>
      </>
    );
  } else if (view === 'hub') {
    inhalt = (
      <>
        <div data-testid="pw-current" style={{ background: c.box, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 12, color: c.sub }}>{t('printer.current')}</div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>🖨️ {drucker?.name || drucker?.address}</div>
          <div style={{ fontSize: 12, color: c.sub, marginTop: 2 }}>{typName(cfg.lang)} · {isNetworkAddress(drucker?.address) ? t('pw.viaNet') : t('pw.viaBt')}</div>
        </div>
        <button type="button" data-testid="pw-test" style={btn(true)} onClick={() => { nachTest.current = 'hub'; setView('test'); sende(drucker.address, guessPrinterLang(drucker?.name) || getPrinterLang(), 1); }}>{t('pw.testPrint')}</button>
        <button type="button" data-testid="pw-label" style={btn(false)} onClick={() => { setCfg(getLabelCfg()); setView('label'); }}>🎨 {t('pw.designLabel')}</button>
        <button type="button" data-testid="pw-autoprint" aria-pressed={autoPrint} style={btn(false, { textAlign: 'left' })}
          onClick={() => { setAutoPrintEnabled(!autoPrint); setAutoPrint(!autoPrint); }}>{autoPrint ? '☑' : '☐'} {t('printer.autoPrint')}</button>
        <button type="button" data-testid="pw-other" style={btn(false)} onClick={() => setView('connect')}>{t('pw.otherPrinter')}</button>
        <button type="button" data-testid="pw-advanced" onClick={() => setErweitert(!erweitert)} style={{ ...btn(false), border: 'none', color: c.sub, fontSize: 13 }}>{erweitert ? '▾' : '▸'} {t('pw.advanced')}</button>
        {erweitert && (
          <div style={{ background: c.box, borderRadius: 12, padding: 12 }}>
            <div style={klein}>{t('pw.typeManual')}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {['cpcl', 'zpl'].map((l) => (
                <button key={l} type="button" data-testid={`pw-type-${l}`} aria-pressed={cfg.lang === l} onClick={() => cfgAendern({ ...cfg, lang: l })}
                  style={{ flex: 1, padding: '10px 6px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    border: `2px solid ${cfg.lang === l ? '#1976d2' : c.rand}`, background: cfg.lang === l ? '#1976d2' : 'transparent', color: cfg.lang === l ? '#fff' : c.text }}>{typName(l)}</button>
              ))}
            </div>
            <div style={{ ...klein, marginTop: 8 }}>{drucker?.address}</div>
            <button type="button" data-testid="pw-remove" style={btn(false, { color: '#e5484d' })} onClick={entfernen}>{t('pw.remove')}</button>
          </div>
        )}
      </>
    );
  } else if (view === 'connect') {
    inhalt = (
      <>
        <div style={klein}>{t('pw.connectIntro')}</div>
        <button type="button" data-testid="pw-via-bt" style={kachel} onClick={() => setView('bt')}>
          <strong>🔵 Bluetooth</strong>
          <div style={{ ...klein, marginTop: 4 }}>{t('pw.btHint')}</div>
        </button>
        <button type="button" data-testid="pw-via-net" style={kachel} onClick={() => setView('net')}>
          <strong>🌐 {t('pw.net')}</strong>
          <div style={{ ...klein, marginTop: 4 }}>{t('pw.netHint')}</div>
        </button>
        {drucker?.address && zurueck('hub')}
        {onClassic && (
          <button type="button" data-testid="pw-classic" onClick={onClassic} style={{ ...btn(false), border: 'none', color: c.sub, fontSize: 13 }}>{t('pw.classic')}</button>
        )}
      </>
    );
  } else if (view === 'bt') {
    const liste = devices || [];
    const drucker1 = liste.filter(erkannt);
    const andere = liste.filter((d) => !erkannt(d));
    const zeile = (d, istDrucker) => (
      <button key={d.address} type="button" data-testid="pw-device" data-name={d.name || ''} onClick={() => waehleGeraet(d)} style={{ ...kachel, borderColor: istDrucker ? '#1976d2' : c.rand }}>
        <strong>{istDrucker ? '🖨️' : '📶'} {d.name || t('printer.unnamed')}</strong>
        <div style={{ fontSize: 12, color: c.sub, marginTop: 2 }}>{istDrucker ? t('pw.recognized') : d.address}</div>
      </button>
    );
    inhalt = (
      <>
        {/* Vorbereitung: was erfüllt sein muss, mit Haken — und was zu tun ist, wenn nicht */}
        <div data-testid="pw-prep" style={{ background: c.box, borderRadius: 12, padding: '10px 12px', marginBottom: 12, fontSize: 14, lineHeight: 1.5 }}>
          <strong>{t('pw.prepTitle')}</strong>
          <div style={{ marginTop: 4 }}>▫️ {t('pw.prepPrinter')}</div>
          <div data-testid="pw-prep-bt" data-ok={btAn ? '1' : '0'} style={{ color: btAn ? c.text : '#e5484d', fontWeight: btAn ? 400 : 700 }}>{btAn ? '✅' : '❌'} {btAn ? t('pw.prepBtOn') : t('pw.prepBtOff')}</div>
          <div data-testid="pw-prep-perm" data-ok={berechtigt ? '1' : '0'} style={{ color: berechtigt ? c.text : '#e5484d', fontWeight: berechtigt ? 400 : 700 }}>{berechtigt ? '✅' : '❌'} {berechtigt ? t('pw.prepPermOk') : t('pw.prepPermMissing')}</div>
          {!berechtigt && (
            <>
              <div style={{ ...klein, marginTop: 4 }}>{t('pw.prepPermHow')}</div>
              <button type="button" data-testid="pw-perm" style={btn(true)} onClick={ladeGeraete}>{t('printer.grantPerm')}</button>
            </>
          )}
          {(!btAn || !berechtigt) && <button type="button" data-testid="pw-prep-check" style={btn(false)} onClick={ladeGeraete}>{t('pw.prepCheck')}</button>}
        </div>
        {devices === null && <div style={klein}>⏳ {t('pw.searching')}</div>}
        {devices !== null && drucker1.length > 0 && <div style={klein}>{t('pw.tapPrinter')}</div>}
        {drucker1.map((d) => zeile(d, true))}
        {devices !== null && drucker1.length === 0 && !fehler && berechtigt && (
          <div data-testid="pw-none" style={{ background: c.box, borderRadius: 12, padding: 12, fontSize: 14, lineHeight: 1.5 }}>
            {t('pw.noneFound')}
            <div style={{ ...klein, marginTop: 6 }}>{t('pw.prepPaired')}</div>
          </div>
        )}
        {andere.length > 0 && !alleZeigen && (
          <button type="button" data-testid="pw-show-all" style={{ ...btn(false), border: 'none', color: c.sub, fontSize: 13 }} onClick={() => setAlleZeigen(true)}>▸ {t('pw.showOthers', { n: andere.length })}</button>
        )}
        {alleZeigen && andere.map((d) => zeile(d, false))}
        {fehler && <div data-testid="pw-error" style={{ color: '#e5484d', fontSize: 13, marginTop: 10, lineHeight: 1.5 }}>⚠️ {fehler}</div>}
        <button type="button" data-testid="pw-refresh" style={btn(false)} onClick={ladeGeraete}>{t('printer.refresh')}</button>

        <button type="button" data-testid="pw-help" onClick={() => setHilfe(!hilfe)} style={{ ...btn(false), border: 'none', color: '#1976d2', fontSize: 14 }}>{hilfe ? '▾' : '▸'} {t('pw.notListed')}</button>
        {hilfe && (
          <div style={{ background: c.box, borderRadius: 12, padding: 12 }}>
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.6 }}>
              <li>{t('pw.help1')}</li>
              <li>{t('pw.help2')}</li>
              <li>{t('pw.help3')}</li>
            </ol>
            <button type="button" data-testid="pw-qr" style={btn(false)} onClick={() => { setFehler(''); setView('qr'); }}>{t('printer.scanQr')}</button>
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <input data-testid="pw-mac" type="text" value={mac} onChange={(e) => setMac(e.target.value)} placeholder={t('pw.macPh')}
                style={{ flex: 1, minWidth: 0, padding: '11px 12px', borderRadius: 10, fontSize: 14, border: `1px solid ${c.rand}`, background: dark ? '#1b1f27' : '#fff', color: c.text, boxSizing: 'border-box' }} />
              <button type="button" data-testid="pw-mac-ok" disabled={!mac.trim()} onClick={() => waehleCode(mac)}
                style={{ padding: '11px 14px', borderRadius: 10, border: 'none', background: '#1976d2', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: mac.trim() ? 1 : 0.5 }}>{t('setup.next')}</button>
            </div>
          </div>
        )}
        {zurueck('connect')}
      </>
    );
  } else if (view === 'net') {
    inhalt = (
      <>
        <div style={klein}>{t('pw.netIntro')}</div>
        <input data-testid="pw-ip" type="text" inputMode="decimal" value={ip} onChange={(e) => setIp(e.target.value)} placeholder="192.168.1.50"
          style={{ width: '100%', padding: '13px', marginTop: 12, borderRadius: 10, fontSize: 18, border: `1px solid ${c.rand}`, background: dark ? '#1b1f27' : '#fff', color: c.text, boxSizing: 'border-box' }} />
        {fehler && <div data-testid="pw-error" style={{ color: '#e5484d', fontSize: 13, marginTop: 8 }}>⚠️ {t('pw.ipInvalid')}</div>}
        <button type="button" data-testid="pw-ip-ok" style={btn(true)} onClick={() => { const p = parsePrinterCode(ip); if (p?.network) waehleCode(ip); else setFehler('ip'); }}>{t('pw.connectTest')}</button>
        {zurueck('connect')}
      </>
    );
  } else if (view === 'qr') {
    inhalt = (
      <>
        <div style={klein}>{t('printer.scanHint')}</div>
        <div style={{ marginTop: 10 }}>
          <ShelfQrScanner active formats={['qr_code', 'data_matrix', 'aztec']} onCode={(code) => { if (!waehleCode(code)) setView('bt'); }} />
        </div>
        {zurueck('bt')}
      </>
    );
  } else if (view === 'test') {
    inhalt = (
      <>
        <div style={{ background: c.box, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 12, color: c.sub }}>{t('printer.current')}</div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>🖨️ {drucker?.name || drucker?.address}</div>
        </div>
        {test.phase === 'sending' && <div data-testid="pw-sending" style={{ ...klein, marginTop: 14, fontSize: 15 }}>⏳ {test.versuch === 2 ? t('pw.sending2') : t('pw.sending')}</div>}
        {test.phase === 'ask' && (
          <>
            <div data-testid="pw-ask" style={{ marginTop: 14, fontSize: 16, fontWeight: 600, lineHeight: 1.4 }}>{t('pw.ask')}</div>
            {test.versuch === 2 && <div style={{ ...klein, marginTop: 4 }}>{t('pw.ask2')}</div>}
            <button type="button" data-testid="pw-yes" style={btn(true, { background: '#2e7d32' })} onClick={antwortJa}>✅ {t('pw.yes')}</button>
            <button type="button" data-testid="pw-no" style={btn(false)} onClick={antwortNein}>❌ {t('pw.no')}</button>
          </>
        )}
        {(test.phase === 'error' || test.phase === 'failed') && (
          <>
            <div data-testid="pw-problem" style={{ marginTop: 14, color: '#e5484d', fontSize: 14, fontWeight: 600, lineHeight: 1.45 }}>
              ⚠️ {test.phase === 'error' ? test.meldung : t('pw.failed')}
            </div>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 14, lineHeight: 1.6 }}>
              <li>{t('pw.tip1')}</li>
              <li>{t('pw.tip2')}</li>
              <li>{t('pw.tip3')}</li>
            </ul>
            <button type="button" data-testid="pw-retry" style={btn(true)} onClick={nochmal}>🔄 {t('pw.retry')}</button>
            <button type="button" data-testid="pw-other" style={btn(false)} onClick={() => setView('connect')}>{t('pw.otherPrinter')}</button>
          </>
        )}
      </>
    );
  } else if (view === 'label') {
    inhalt = (
      <>
        <LabelDesigner value={cfg} onChange={cfgAendern} t={t} colors={colors} nachnameErlaubt={hasCustomFeature('nachnameSortierung')} />
        {drucker?.address && <button type="button" data-testid="pw-label-test" style={btn(false)} onClick={() => testPrint(drucker.address, { direkt: true }).catch((e) => setFehler(errText(e, t('printer.testFailed'))))}>🖨️ {t('pw.testThis')}</button>}
        {fehler && <div data-testid="pw-error" style={{ color: '#e5484d', fontSize: 13, marginTop: 8 }}>⚠️ {fehler}</div>}
        <button type="button" data-testid="pw-label-done" style={btn(true)} onClick={() => { setFehler(''); setView(drucker?.address ? 'hub' : 'connect'); }}>{t('pw.done')}</button>
      </>
    );
  }

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div data-testid="printer-wizard" data-view={supported ? view : 'unsupported'} style={{ background: c.bg, color: c.text, borderRadius: 14, width: '100%', maxWidth: 440, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${c.rand}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong style={{ fontSize: 16 }}>🖨️ {supported ? titel : t('pw.labelTitle')}</strong>
          <button type="button" data-testid="pw-close" onClick={onClose} aria-label={t('common.cancel')} style={{ background: 'none', border: 'none', fontSize: 24, lineHeight: 1, cursor: 'pointer', color: c.text }}>×</button>
        </div>
        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>{inhalt}</div>
      </div>
    </div>
  );
}
